import React, { useState } from 'react';
import {
  DataGrid,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { Button } from 'react-bootstrap';
import { ReleaseResponse, ReleaseTableProps } from '../types';
import { useNavigate } from 'react-router-dom';
import { getUiInterface } from '../../../locales/translationUtils';

const ReleaseTable: React.FC<ReleaseTableProps> = ({ 
  apiResponse,
  rows, 
  columns,
  paginationModel, 
  setPaginationModel
 }) => {
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState<ReleaseResponse[]>([]);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [firstSelectedSupplier, setFirstSelectedSupplier] = useState<
    string | undefined
  >(undefined);

  const onToRelease = () => {
    navigate('/release/to-release', { state: { selectedRows } });
  };

  const onToClean = () => {
    setSelectedRows([]);
    setFirstSelectedSupplier(undefined);
    setRowSelectionModel([]);
  };

  function handleSelectionChange(ids: GridRowSelectionModel) {
    if (ids.length === 0) {
      setFirstSelectedSupplier(undefined);
    }
    const selectedIDs = new Set(ids);
    const firstSelected = ids[0];
    const firstSelectedRow = rows.find((row) => row.id === firstSelected);

    if (selectedRows.length === 0) {
      setFirstSelectedSupplier(firstSelectedRow?.supplierName);
    }

    const selectedRow = rows.filter((row) => {
      return selectedIDs.has(row.id);
    });

    setSelectedRows(selectedRow);
    setRowSelectionModel(ids);
  }

  function isRowSelectable(params: GridRowParams) {
    return (
      firstSelectedSupplier === undefined ||
      params.row.supplierName === firstSelectedSupplier
    );
  }

  return (
    <div style={{ height: 350, width: '100%', marginTop: -10 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSizeOptions={[5, 10, 20, 100]}
        pagination
        paginationMode='server'
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowCount={apiResponse?.data?.totalItems ?? 0}
        checkboxSelection
        disableRowSelectionOnClick
        onRowSelectionModelChange={handleSelectionChange}
        rowSelectionModel={rowSelectionModel}
        getRowClassName={(params) => {
          const fromValue = params.row.fromIdentification;
          const rowIndex = params.indexRelativeToCurrentPage; 

          if (fromValue === 'LAX') {
            return rowIndex % 2 === 0 ? 'highlight-lax-even' : 'highlight-lax-odd';
          }
          if (fromValue === 'CVG') {
            return rowIndex % 2 === 0 ? 'highlight-cvg-even' : 'highlight-cvg-odd';
          }
          if (fromValue === 'DFW') {
            return rowIndex % 2 === 0 ? 'highlight-dfw-even' : 'highlight-dfw-odd';
          }
          if (fromValue === 'MIA') {
            return rowIndex % 2 === 0 ? 'highlight-mia-even' : 'highlight-mia-odd';
          }

          return '';
        }}
        sx={{
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-row:nth-of-type(odd)': {
            backgroundColor: 'white',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#b2d1ff',
          },
          '& .highlight-lax-even': {
            backgroundColor: '#2600A0 !important',
            color: '#FFFFFF !important',
          },
          '& .highlight-lax-odd': {
            backgroundColor: '#0004B9 !important',
            color: '#FFFFFF !important',
          },

          '& .highlight-cvg-even': {
            backgroundColor: '#89E17E !important',
          },
          '& .highlight-cvg-odd': {
            backgroundColor: '#B1EEB6 !important',
          },
          '& .highlight-dfw-even': {
            backgroundColor: '#fff1c1 !important',
          },
          '& .highlight-dfw-odd': {
            backgroundColor: '#fff7a8 !important',
          },
          '& .highlight-mia-even': {
            backgroundColor: '#C6C3BE !important',
          },
          '& .highlight-mia-odd': {
            backgroundColor: '#FFFCF8 !important',
          },
          '& .MuiTablePagination-selectLabel': {
            marginTop: '14px',
          },
          '& .MuiTablePagination-displayedRows': {
            marginTop: '14px',
          },
          '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
            display: 'none',
          },
          "& .MuiDataGrid-cell[id='id']": {
            cursor: 'pointer !important',
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-row:focus, & .MuiDataGrid-row:focus-within": {
            outline: "none",
          }
        }}
        isRowSelectable={isRowSelectable}
      />

      <div style={{ marginTop: 16, paddingBottom: 16, textAlign: 'right' }}>
        <Button
          variant="primary"
          onClick={onToClean}
          disabled={selectedRows.length === 0}
        >
          {getUiInterface('clean')}
        </Button>

        <span style={{ margin: '0 8px' }} />

        <Button
          variant="primary"
          onClick={onToRelease}
          disabled={selectedRows.length === 0}
        >
          {getUiInterface('toRelease')}
        </Button>
      </div>
    </div>
  );
};

export default ReleaseTable;
